<template>
  <div class="page">
    <div class="container">
      <div class="breadcrumb">
        <span class="crumb" @click="$router.push('/dashboard')">Home</span>
        <i class="el-icon-arrow-right sep" />
        <span class="crumb" @click="$router.push('/dashboard')">Dashboard</span>
        <i class="el-icon-arrow-right sep" />
        <span class="crumb-current">Webshare Proxies</span>
      </div>

      <div class="head">
        <div>
          <div class="title">Webshare Proxy Management</div>
          <div class="sub">
            Monitor active proxies, track bandwidth consumption, and manage device concurrency in real-time.
          </div>
        </div>
        <div class="head-actions">
          <el-button size="small" @click="onExport">
            <i class="el-icon-download" />
            <span>Export CSV</span>
          </el-button>
        </div>
      </div>

      <div class="kpis">
        <div class="kpi-card">
          <div class="kpi-top">
            <div class="kpi-label">Total Active Proxies</div>
            <div class="kpi-icon blue"><i class="el-icon-share" /></div>
          </div>
          <div class="kpi-value">{{ kpi.totalProxies }}</div>
        </div>

        <div class="kpi-card">
          <div class="kpi-top">
            <div class="kpi-label">Total Bandwidth</div>
            <div class="kpi-icon purple"><i class="el-icon-data-analysis" /></div>
          </div>
          <div class="kpi-value">{{ kpi.bandwidthTotal }}</div>

        </div>

        <div class="kpi-card">
          <div class="kpi-top">
            <div class="kpi-label">Bandwidth Limit</div>
            <div class="kpi-icon orange"><i class="el-icon-connection" /></div>
          </div>
          <div class="kpi-value">{{ kpi.bandwidthLimit }}</div>
        </div>

        <div class="kpi-card">
          <div class="kpi-top">
            <div class="kpi-label">Requests Total</div>
            <div class="kpi-icon red"><i class="el-icon-warning-outline" /></div>
          </div>
          <div class="kpi-value">{{ kpi.requestsTotal }}</div>
        </div>
      </div>

      <div class="table-card">
        <div class="table-toolbar">
          <el-input v-model="keyword" size="small" clearable class="search"
            placeholder="Search by Device ID, IP Address, or User" prefix-icon="el-icon-search"
            @keyup.enter.native="onApply" />

          <div class="toolbar-right">
            <el-select v-model="filters.status" size="small" clearable placeholder="Status: All" class="status"
              @change="onApply">
              <el-option label="Active" value="active" />
              <el-option label="Closed" value="closed" />
            </el-select>

            <el-button size="small" class="ghost" @click="onOpenFilter">
              <i class="el-icon-filter" />
              <span>Filter</span>
            </el-button>
            <el-button size="small" class="ghost" @click="onOpenSort">
              <i class="el-icon-sort" />
              <span>Sort</span>
            </el-button>
          </div>
        </div>

        <el-table ref="table" v-loading="loading" :data="rows" :height="tableHeight" stripe class="proxies-table"
          style="width: 100%" :empty-text="'No proxy data'">
          <el-table-column label="Device" min-width="220">
            <template slot-scope="scope">
              <div class="device-cell">
                <div class="device-name">{{ scope.row.device_name || '-' }}</div>
                <div class="device-sub">ID: {{ scope.row.device_id || '-' }}</div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="Endpoint (IP:Port)" min-width="260">
            <template slot-scope="scope">
              <div class="endpoint-cell">
                <span class="mono">{{ scope.row.endpoint || '-' }}</span>
                <el-button v-if="scope.row.endpoint" type="text" class="icon-btn" :title="copyTitle(scope.row)"
                  @click="copyEndpointOrProxy(scope.row)">
                  <i class="el-icon-copy-document"></i>
                </el-button>
              </div>
              <div class="endpoint-sub" v-if="scope.row.username || scope.row.password">
                user: <span class="mono">{{ scope.row.username || '-' }}</span>
                • pass: <span class="mono">{{ scope.row.password || '—' }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="Concurrency" width="160" align="center">
            <template slot-scope="scope">
              <span class="pill">{{ Number(scope.row.concurrency || 0) }}</span>
              <span class="muted tiny" v-if="scope.row.concurrency">threads</span>
            </template>
          </el-table-column>

          <el-table-column label="Status" width="160">
            <template slot-scope="scope">
              <span class="status-pill" :class="statusClass(scope.row)">
                <span class="dot" />
                {{ statusLabel(scope.row) }}
              </span>
            </template>
          </el-table-column>
        </el-table>

        <div class="footer">
          <div class="footer-left">
            Showing <span class="footer-strong">{{ showingFrom }}</span> to <span class="footer-strong">{{
              showingTo
            }}</span> of <span class="footer-strong">{{ total }}</span> results
          </div>
          <div class="footer-right">
            <el-pagination :current-page="currentPage" :page-size="limit" :page-sizes="[10, 20, 50]" :total="total"
              layout="sizes, prev, pager, next" @size-change="onSizeChange" @current-change="onCurrentChange" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getExternalSessions, getExternalSessionsSummary } from '../../api/sessions'

export default {
  name: 'ProxiesView',
  data() {
    return {
      loading: false,
      rows: [],
      total: 0,
      limit: 20,
      offset: 0,
      tableHeight: 420,
      keyword: '',
      filters: {
        status: ''
      },
      kpi: {
        totalProxies: '--',
        bandwidthTotal: '--',
        bandwidthLimit: '--',
        requestsTotal: '--'
      },
      _bandwidthTotalRaw: null,
      _bandwidthLimitRaw: null
    }
  },
  computed: {
    currentPage() {
      return Math.floor((this.offset || 0) / (this.limit || 1)) + 1
    },
    showingFrom() {
      if (!this.total) return 0
      return this.offset + 1
    },
    showingTo() {
      return Math.min(this.offset + (this.rows ? this.rows.length : 0), this.total)
    },
    bandwidthPct() {
      const total = Number(this._bandwidthTotalRaw || 0)
      const limit = Number(this._bandwidthLimitRaw || 0)
      if (!limit || total <= 0) return 0
      return Math.max(0, Math.min(100, Math.round((total / limit) * 100)))
    }
  },
  mounted() {
    this.fetchKpis()
    this.fetchRows()
    this.$nextTick(this.calcTableHeight)
    window.addEventListener('resize', this.calcTableHeight, false)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calcTableHeight, false)
  },
  methods: {
    calcTableHeight() {
      try {
        const el = this.$refs.table && this.$refs.table.$el ? this.$refs.table.$el : null
        if (!el) return
        const rect = el.getBoundingClientRect()
        const footer = this.$el ? this.$el.querySelector('.footer') : null
        const footerH = footer ? footer.getBoundingClientRect().height : 0
        const paddingBottom = 24
        const h = Math.floor(window.innerHeight - rect.top - footerH - paddingBottom)
        this.tableHeight = Math.max(260, h)
      } catch (e) { }
    },
    async fetchKpis() {
      try {
        const s = await getExternalSessionsSummary()
        const totalProxies = (s && typeof s.total_proxies === 'number') ? s.total_proxies : null
        const bandwidthTotal = (s && typeof s.bandwidth_total === 'number') ? s.bandwidth_total : null
        const bandwidthLimit = (s && typeof s.bandwidth_limit === 'number') ? s.bandwidth_limit : null
        const requestsTotal = (s && typeof s.requests_total === 'number') ? s.requests_total : null

        this.kpi.totalProxies = (totalProxies == null) ? '--' : String(totalProxies)
        this.kpi.bandwidthTotal = (bandwidthTotal == null) ? '--' : this.fmtBytes(bandwidthTotal)
        this.kpi.bandwidthLimit = (bandwidthLimit == null) ? '--' : String(bandwidthLimit)
        this.kpi.requestsTotal = (requestsTotal == null) ? '--' : String(requestsTotal)

        this._bandwidthTotalRaw = bandwidthTotal
        this._bandwidthLimitRaw = bandwidthLimit
      } catch (e) { }
    },
    async fetchRows() {
      this.loading = true
      try {
        const data = await getExternalSessions({
          limit: this.limit,
          offset: this.offset,
          status: this.filters.status,
          keyword: this.keyword
        })

        const sessions = (data && data.sessions) ? data.sessions : []
        this.rows = sessions.map(s => {
          const peer = s && s.consumer_peer ? s.consumer_peer : null
          const proxy = s && s.external_proxy ? s.external_proxy : null
          const host = proxy && proxy.host ? String(proxy.host) : ''
          const port = (proxy && (proxy.port || proxy.port === 0)) ? String(proxy.port) : ''
          const endpoint = (host && port) ? (host + ':' + port) : (host || '')

          const bytesIn = Number(s && s.bytes_in || 0)
          const bytesOut = Number(s && s.bytes_out || 0)
          const used = bytesIn + bytesOut

          return {
            id: s && s.id,
            device_name: (peer && peer.name) ? peer.name : ((s && s.consumer_peer_id) || '-'),
            device_id: (peer && peer.id) ? peer.id : ((s && s.consumer_peer_id) || '-'),
            endpoint,
            username: proxy && proxy.username ? proxy.username : '',
            password: proxy && proxy.password ? String(proxy.password) : '',
            concurrency: null,
            status: s && s.status ? s.status : ''
          }
        })
        this.total = (data && typeof data.total === 'number') ? data.total : (this.offset + this.rows.length)
      } catch (e) {
        this.$message && this.$message.error('Không tải được external sessions')
      } finally {
        this.loading = false
        this.$nextTick(this.calcTableHeight)
      }
    },
    onExport() {
      this.$message && this.$message.info('Export CSV (demo)')
    },
    onOpenFilter() {
      this.onApply()
    },
    onOpenSort() {
      this.$message && this.$message.info('Sort (chưa có API list proxies)')
    },
    onApply() {
      this.offset = 0
      this.fetchRows()
    },
    onSizeChange(size) {
      this.limit = size
      this.offset = 0
      this.fetchRows()
    },
    onCurrentChange(page) {
      const p = Number(page || 1)
      this.offset = Math.max(0, (p - 1) * this.limit)
      this.fetchRows()
    },
    async copyText(text) {
      const t = String(text || '')
      if (!t) return
      try {
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(t)
        } else {
          const ta = document.createElement('textarea')
          ta.value = t
          ta.setAttribute('readonly', '')
          ta.style.position = 'absolute'
          ta.style.left = '-9999px'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.$message && this.$message.success('Copied')
      } catch (e) {
        this.$message && this.$message.error('Copy failed')
      }
    },
    fmtBytes(n) {
      const num = Number(n || 0)
      const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
      let v = num
      let i = 0
      while (v >= 1024 && i < units.length - 1) {
        v = v / 1024
        i++
      }
      return (i === 0 ? String(Math.round(v)) : v.toFixed(2)) + ' ' + units[i]
    },
    proxyString(row) {
      if (!row) return ''
      const endpoint = row.endpoint || ''
      const u = row.username || ''
      const p = row.password || ''
      if (!endpoint) return ''
      return endpoint + ':' + u + ':' + p
    },
    copyTitle(row) {
      const full = this.proxyString(row)
      return full ? 'Copy proxy ip:port:user:pass' : 'Copy endpoint'
    },
    copyEndpointOrProxy(row) {
      const full = this.proxyString(row)
      if (full) return this.copyText(full)
      return this.copyText(row && row.endpoint ? row.endpoint : '')
    },
    statusLabel(row) {
      const s = (row && row.status) ? String(row.status) : ''
      if (s === 'active' || s === 'online') return 'Active'
      if (s === 'inactive' || s === 'offline') return 'Inactive'
      return s ? s : '—'
    },
    statusClass(row) {
      const s = this.statusLabel(row)
      if (s === 'Active') return 'is-active'
      if (s === 'Inactive') return 'is-inactive'
      return ''
    }
  }
}
</script>

<style scoped>
.page {
  padding: 16px;
}

.container {
  max-width: 1600px;
  margin: 0 auto;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #92a4c9;
  margin-bottom: 14px;
}

.crumb {
  cursor: pointer;
  color: #92a4c9;
}

.crumb:hover {
  color: #135bec;
}

.crumb-current {
  color: #ffffff;
  font-weight: 700;
}

.sep {
  font-size: 12px;
  opacity: 0.8;
}

.head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}

.title {
  font-size: 28px;
  font-weight: 900;
  color: #ffffff;
}

.sub {
  margin-top: 8px;
  color: #92a4c9;
  max-width: 900px;
  line-height: 1.5;
}

.head-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.kpis {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

@media (min-width: 640px) {
  .kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .kpis {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.kpi-card {
  background: #1a2235;
  border: 1px solid #232f48;
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.25);
}

.kpi-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.kpi-label {
  color: #92a4c9;
  font-weight: 700;
  font-size: 12px;
}

.kpi-value {
  margin-top: 10px;
  font-size: 22px;
  font-weight: 900;
}

.kpi-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.kpi-icon.blue {
  background: rgba(59, 130, 246, 0.15);
  color: #60a5fa;
}

.kpi-icon.purple {
  background: rgba(168, 85, 247, 0.15);
  color: #c084fc;
}

.kpi-icon.orange {
  background: rgba(249, 115, 22, 0.15);
  color: #fb923c;
}

.kpi-icon.red {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.kpi-bar {
  margin-top: 10px;
}

.bar-track {
  width: 100%;
  height: 6px;
  background: #111722;
  border: 1px solid #232f48;
  border-radius: 999px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: #a855f7;
  border-radius: 999px;
}

.kpi-hint {
  margin-top: 6px;
  color: #92a4c9;
  font-size: 12px;
}

.table-card {
  background: #1a2235;
  border: 1px solid #232f48;
  border-radius: 12px;
  overflow: hidden;
}

.table-toolbar {
  padding: 12px;
  border-bottom: 1px solid #232f48;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.search {
  min-width: 320px;
  flex: 1;
}

.toolbar-right {
  display: flex;
  gap: 10px;
}

.status {
  min-width: 140px;
}

.ghost {
  background: transparent;
  border: 1px solid #232f48;
  color: #cbd5e1;
}

.ghost:hover {
  border-color: #135bec;
  color: #ffffff;
}

.device-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.device-name {
  font-weight: 900;
}

.device-sub {
  font-size: 12px;
  color: #92a4c9;
}

.endpoint-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.endpoint-sub {
  font-size: 12px;
  color: #92a4c9;
  margin-top: 4px;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.icon-btn {
  padding: 0 6px;
}

.strong {
  font-weight: 900;
}

.muted {
  color: #92a4c9;
}

.tiny {
  font-size: 12px;
  margin-left: 6px;
}

.pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  background: rgba(59, 130, 246, 0.15);
  color: #93c5fd;
  border: 1px solid rgba(59, 130, 246, 0.25);
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 800;
  font-size: 12px;
  border: 1px solid #232f48;
  background: rgba(148, 163, 184, 0.1);
  color: #cbd5e1;
}

.status-pill .dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #94a3b8;
}

.status-pill.is-active {
  background: rgba(34, 197, 94, 0.12);
  border-color: rgba(34, 197, 94, 0.25);
  color: #86efac;
}

.status-pill.is-active .dot {
  background: #22c55e;
}

.status-pill.is-inactive {
  background: rgba(148, 163, 184, 0.12);
  border-color: rgba(148, 163, 184, 0.25);
  color: #cbd5e1;
}

.footer {
  border-top: 1px solid #324467;
  background: #161e2c;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.footer-left {
  color: #92a4c9;
  font-size: 12px;
}

.footer-strong {
  color: #ffffff;
  font-weight: 800;
}

.footer-right /deep/ .el-pager li {
  background: transparent;
  color: #92a4c9;
}

.footer-right /deep/ .el-pager li.active {
  color: #ffffff;
  background: #135bec;
  border-radius: 8px;
}

.footer-right /deep/ .btn-prev,
.footer-right /deep/ .btn-next {
  background: #232f48;
  color: #ffffff;
  border-radius: 8px;
}
</style>



// WEBPACK FOOTER //
// src/views/proxies/index.vue